import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import type { Difficulty } from "../ai/bot";
import type { BidValue, Card, Ruleset, Suit, TeamId } from "../types/game";
import type { StatsHandSlice } from "./playerStatsLogic";

// Thin typed wrappers around our callable Cloud Functions. Keep the shapes
// in sync with functions/src/rooms.ts.

export async function callCreateRoom(
  args: { ruleset?: Ruleset } = {},
): Promise<{ roomId: string; code: string }> {
  const fn = httpsCallable<
    { ruleset?: Ruleset },
    { roomId: string; code: string }
  >(functions, "createRoom");
  const res = await fn(args);
  return res.data;
}

export async function callSetRoomRuleset(args: {
  roomId: string;
  ruleset: Ruleset;
}): Promise<{ ok: true }> {
  const fn = httpsCallable<typeof args, { ok: true }>(functions, "setRoomRuleset");
  const res = await fn(args);
  return res.data;
}

export async function callJoinRoomByCode(code: string): Promise<{ roomId: string }> {
  const fn = httpsCallable<{ code: string }, { roomId: string }>(functions, "joinRoomByCode");
  const res = await fn({ code });
  return res.data;
}

export async function callLeaveRoom(roomId: string): Promise<{ ok: true }> {
  const fn = httpsCallable<{ roomId: string }, { ok: true }>(functions, "leaveRoom");
  const res = await fn({ roomId });
  return res.data;
}

export async function callSetSeat(args: {
  roomId: string;
  seatIndex: 0 | 1 | 2 | 3;
  kind: "bot" | "empty";
  difficulty?: Difficulty;
}): Promise<{ ok: true }> {
  const fn = httpsCallable<typeof args, { ok: true }>(functions, "setSeat");
  const res = await fn(args);
  return res.data;
}

export async function callMoveSelf(args: {
  roomId: string;
  seatIndex: 0 | 1 | 2 | 3;
}): Promise<{ ok: true }> {
  const fn = httpsCallable<typeof args, { ok: true }>(functions, "moveSelf");
  const res = await fn(args);
  return res.data;
}

export async function callStartMatch(roomId: string): Promise<{ matchId: string }> {
  const fn = httpsCallable<{ roomId: string }, { matchId: string }>(functions, "startMatch");
  const res = await fn({ roomId });
  return res.data;
}

/** Same four seats in a brand-new room; fresh 0–0 match. Host-only. */
export async function callStartRematch(matchId: string): Promise<{ matchId: string; roomId: string; code: string }> {
  const fn = httpsCallable<{ matchId: string }, { matchId: string; roomId: string; code: string }>(
    functions,
    "startRematch",
  );
  const res = await fn({ matchId });
  return res.data;
}

export async function callSubmitBid(matchId: string, value: BidValue | "pass"): Promise<{ ok: true }> {
  const fn = httpsCallable<{ matchId: string; value: BidValue | "pass" }, { ok: true }>(functions, "submitBid");
  const res = await fn({ matchId, value });
  return res.data;
}

export async function callSubmitTrumpChoice(matchId: string, suit: Suit): Promise<{ ok: true }> {
  const fn = httpsCallable<{ matchId: string; suit: Suit }, { ok: true }>(functions, "submitTrumpChoice");
  const res = await fn({ matchId, suit });
  return res.data;
}

export async function callSubmitPlay(matchId: string, card: Card): Promise<{ ok: true }> {
  const fn = httpsCallable<{ matchId: string; card: Card }, { ok: true }>(functions, "submitPlay");
  const res = await fn({ matchId, card });
  return res.data;
}

export async function callClaimCoke(matchId: string): Promise<{ ok: true }> {
  const fn = httpsCallable<{ matchId: string }, { ok: true }>(functions, "claimCoke");
  const res = await fn({ matchId });
  return res.data;
}

export async function callDealNextHand(matchId: string): Promise<{ ok: true }> {
  const fn = httpsCallable<{ matchId: string }, { ok: true }>(functions, "dealNextHand");
  const res = await fn({ matchId });
  return res.data;
}

export async function callNudgeIdleTurn(matchId: string): Promise<{ played: boolean; reason?: string }> {
  const fn = httpsCallable<
    { matchId: string },
    { played: boolean; reason?: string }
  >(functions, "nudgeIdleTurn");
  const res = await fn({ matchId });
  return res.data;
}

export async function callUpdateScreenName(screenName: string): Promise<{ ok: true; screenName: string }> {
  const fn = httpsCallable<{ screenName: string }, { ok: true; screenName: string }>(
    functions,
    "updateScreenName",
  );
  const res = await fn({ screenName });
  return res.data;
}

export async function callRecordSoloStats(args: {
  idempotencyKey: string;
  hand?: StatsHandSlice;
  gameWinner?: TeamId;
}): Promise<{ ok: true }> {
  const fn = httpsCallable<typeof args, { ok: true }>(functions, "recordSoloStats");
  const res = await fn(args);
  return res.data;
}
